/**
 * QR-код, отрисованный как inline-SVG из матрицы модулей (generateQrMatrix).
 *
 * SVG (а не canvas) — резкий на любом DPR, печатается и масштабируется без
 * размытия, не требует ref/effect. Quiet-zone (рамка) — 4 модуля по стандарту.
 */
import { useMemo } from 'react';
import { generateQrMatrix } from '../../lib/qr.js';

export interface QrCanvasProps {
  /** Кодируемая строка (deep-link). */
  value: string;
  /** Сторона в пикселях (по умолчанию 220). */
  size?: number;
  /** data-testid для тестов/скриншотов. */
  testId?: string;
}

const QUIET = 4; // модулей рамки по стандарту QR

export function QrCanvas({ value, size = 220, testId }: QrCanvasProps): JSX.Element {
  const matrix = useMemo(() => {
    try {
      return generateQrMatrix(value);
    } catch {
      return null;
    }
  }, [value]);

  if (!matrix || matrix.length === 0) {
    return (
      <div className="ga-qr ga-qr--err" data-testid={testId} role="img" aria-label="QR-код">
        Не удалось построить QR-код
      </div>
    );
  }

  const n = matrix.length;
  const total = n + QUIET * 2;
  const rects: JSX.Element[] = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (matrix[r]![c]) {
        rects.push(
          <rect key={`${r}-${c}`} x={c + QUIET} y={r + QUIET} width={1} height={1} />,
        );
      }
    }
  }

  return (
    <svg
      className="ga-qr"
      width={size}
      height={size}
      viewBox={`0 0 ${total} ${total}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QR-код для входа в Telegram-бота отеля"
      data-testid={testId}
    >
      <rect x={0} y={0} width={total} height={total} fill="#ffffff" />
      <g fill="#1a1a1a">{rects}</g>
    </svg>
  );
}
